import {
  Checkbox,
  FormControlLabel,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { Controller, useForm } from 'react-hook-form';

import { selectOrganisationInfos } from '@/domain/organisation/organisation.selectors';
import { ProjectFestival } from '@/domain/project-festival/entities/project-festival.entity';
import { FSDateField } from '@/ui/components/input/FSDateField';
import { FSImageField } from '@/ui/components/input/FSImageField';
import { UploadImageServiceS3 } from '@/ui/components/input/FSImageField.utils/upload-image-service-s3';
import { FSTextField } from '@/ui/components/input/FSTextField';
import { useAppSelector } from '@/ui/hook/store';
import { translate } from '@/ui/i18n';
import { isAtLeastStartDate } from '@/ui/utils/form-validations';

interface IProps {
  project: ProjectFestival;
  type: 'create' | 'edit';
  step?: number;
  buttons?: React.ReactNode;
  onSubmit: (data: ProjectFestival) => Promise<void> | void;
}

export function ProjectFestivalForm({
  project,
  type,
  step,
  buttons,
  onSubmit,
}: IProps) {
  const organisationId = useAppSelector(selectOrganisationInfos).id.toString();
  const imageDestinationFolder = organisationId;

  const { handleSubmit, register, formState, control, getValues, watch } =
    useForm<ProjectFestival>({
      defaultValues: project,
    });

  const { errors } = formState;

  const handleFormSubmit = (data: ProjectFestival) => {
    onSubmit({
      ...data,
      avgCapacity: parseInt(data.avgCapacity as unknown as string),
    });
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} noValidate>
      <Stack>
        {/* DISPLAY IF EDITING OR CREATING AND STEP === 1 */}
        {((type === 'create' && step === 1) || type === 'edit') && (
          <>
            <Controller
              name="image"
              control={control}
              render={({ field }) => (
                <FSImageField
                  name={field.name}
                  value={field.value}
                  onChange={field.onChange}
                  uploadImageService={
                    new UploadImageServiceS3(imageDestinationFolder)
                  }
                />
              )}
            />

            <FSTextField
              label={translate('Projects.Form.project-name')}
              defaultValue={project.name}
              register={{
                ...register('name', {
                  required: translate('Projects.Form.name-is-required'),
                }),
              }}
              error={!!errors.name}
              helperText={errors.name?.message}
            />

            <Stack sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
              <FSDateField
                sx={{ width: '100%' }}
                value={project.startDate || ''}
                label={translate('Form.start-date')}
                control={control}
                name="startDate"
                error={!!errors.startDate}
                helperText={errors.startDate?.message}
              />

              <FSDateField
                sx={{ width: '100%' }}
                value={project.endDate || ''}
                label={translate('Form.end-date')}
                minDate={dayjs(watch('startDate'))}
                control={control}
                name="endDate"
                error={!!errors.endDate}
                helperText={errors.endDate?.message}
                validate={(date) =>
                  isAtLeastStartDate(date, getValues('startDate') || '')
                }
              />
            </Stack>

            <FSTextField
              defaultValue={project.location}
              label={translate('Projects.Form.location')}
              register={{
                ...register('location'),
              }}
              error={!!errors.location}
              helperText={errors.location?.message}
            />
          </>
        )}

        {/* DISPLAY IF EDITING OR CREATING AND STEP === 2 */}
        {((type === 'create' && step === 2) || type === 'edit') && (
          <>
            <FSTextField
              select
              defaultValue={project.venue}
              label={translate('Projects.Form.venue-field')}
              register={{
                ...register('venue'),
              }}
              error={!!errors.venue}
              helperText={errors.venue?.message}
            >
              <MenuItem value="ON_PREMISES">
                {translate('Projects.Form.on-premises')}
              </MenuItem>
              <MenuItem value="OFF_PREMISES">
                {translate('Projects.Form.off-premises')}
              </MenuItem>
              <MenuItem value="OUTDOOR">
                {translate('Projects.Form.outdoor')}
              </MenuItem>
            </FSTextField>
            <FSTextField
              type="number"
              label={translate('Projects.Form.avg-capacity')}
              defaultValue={project.avgCapacity}
              maxLength={5}
              register={{
                ...register('avgCapacity'),
              }}
              error={!!errors.avgCapacity}
              helperText={errors.avgCapacity?.message}
            />
            <Stack direction={'column'}>
              <FormControlLabel
                sx={{ mr: 'auto' }}
                label={
                  <Typography>
                    {translate('Projects.Form.access-to-energy')}
                  </Typography>
                }
                control={
                  <Checkbox
                    {...register('accessToEnergy')}
                    checked={watch('accessToEnergy')}
                  />
                }
              />
              <FormControlLabel
                sx={{ mr: 'auto' }}
                label={
                  <Typography>
                    {translate('Projects.Form.access-to-water')}
                  </Typography>
                }
                control={
                  <Checkbox
                    {...register('accessToWater')}
                    checked={watch('accessToWater')}
                  />
                }
              />
            </Stack>
          </>
        )}
      </Stack>
      {buttons && buttons}
    </form>
  );
}
